import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
	Flex,
	FlexItem,
	FormGroup,
	GridItem,
	SearchInput,
	Text,
	Title,
	Switch,
} from '@patternfly/react-core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSparkles } from '@fortawesome/pro-solid-svg-icons';
import { faInfoCircle } from '@fortawesome/pro-regular-svg-icons';
import Loader from '../../components/util/Loader';
import EmptyResults from '../../components/EmptyResults';
import DatePeriodSelector from '../../components/date-period-selector/DatePeriodSelector';
import Question from './Question';
import { CategoryQuestion, DiscoverCategory } from '../../types/discover';
import { TNewDateRange } from '../../api/types/TNewDateRange';
import { TQuestion } from '../../api/questions/Questions';
import { TTag } from '../../api/tags/Tags';

// Synonym map — keys and values are all lowercase
const SYNONYM_MAP: Record<string, string[]> = {
	compensation: ['compensate', 'compensated', 'pay', 'salary', 'wage', 'remuneration'],
	compensate: ['compensation', 'pay', 'salary', 'wage'],
	pay: ['compensation', 'compensate', 'salary', 'wage'],
	salary: ['pay', 'compensation', 'wage', 'compensate'],
	hire: ['hiring', 'hired', 'recruitment', 'recruit', 'onboard'],
	hiring: ['hire', 'hired', 'recruitment', 'recruit'],
	recruitment: ['hire', 'hiring', 'recruit', 'onboard'],
	turnover: ['attrition', 'churn', 'resignation', 'resign'],
	attrition: ['turnover', 'churn', 'resignation'],
	performance: ['perform', 'review', 'appraisal', 'evaluation'],
	headcount: ['employees', 'staff', 'workforce', 'personnel', 'count'],
	employee: ['employees', 'staff', 'worker', 'personnel', 'headcount'],
	diversity: ['inclusion', 'equity', 'dei', 'belonging'],
	training: ['learning', 'development', 'upskill', 'education'],
	engagement: ['satisfaction', 'morale', 'sentiment', 'wellbeing'],
	absence: ['absenteeism', 'leave', 'sick', 'time off'],
	leave: ['absence', 'absenteeism', 'sick', 'time off'],
};

const expandTerms = (term: string): string[] => {
	const lower = term.toLowerCase();
	const synonyms = SYNONYM_MAP[lower] ?? [];
	return [lower, ...synonyms];
};

const matchesSearch = (target: string, expandedTerms: string[]): boolean => {
	const lower = target.toLowerCase();
	return expandedTerms.some((t) => lower.includes(t));
};

type CategorySearchResult = {
	category: DiscoverCategory;
	matchedQuestions: TQuestion[];
	categoryNameMatches: boolean;
};

interface DiscoverSearchProps {
	searchTerm: string;
	setSearchTerm: (v: string) => void;
	aiSearchEnabled: boolean;
	setAiSearchEnabled: (b: boolean) => void;
	categories?: DiscoverCategory[];
	allQuestions?: TQuestion[];
	hasLoadedCategories?: boolean;
	openCategory: (id: number) => void;
	setSelectedDate: (d?: TNewDateRange) => void;
	isOnSearchRoute: boolean;
}

const DiscoverSearch: React.FC<DiscoverSearchProps> = ({
	searchTerm,
	setSearchTerm,
	aiSearchEnabled,
	setAiSearchEnabled,
	categories,
	allQuestions,
	hasLoadedCategories,
	openCategory,
	setSelectedDate,
	isOnSearchRoute,
}) => {
	const navigate = useNavigate();
	const isSearchActive = searchTerm.trim().length >= 3;
	const [searchDelayActive, setSearchDelayActive] = useState(false);
	const isOnHomePage =
		window.location.pathname === '/' || window.location.pathname == '/getting-started';

	const handleSearch = (_event: any, value: string) => {
		if (aiSearchEnabled) {
			navigate(`/discover/analyst?term=${value}`);
		} else {
			navigate(`/discover/search?term=${value}`);
		}
	};

	const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
		if (e.key === 'Enter' && searchTerm && searchTerm.trim().length > 0) {
			if (aiSearchEnabled) {
				navigate(`/discover/analyst?term=${searchTerm}`);
			} else {
				navigate(`/discover/search?term=${searchTerm}`);
			}
		}
	};

	const searchResults = useMemo<CategorySearchResult[]>(() => {
		if (!isSearchActive || !categories || !allQuestions) return [];

		const words = searchTerm.trim().toLowerCase().split(/\s+/);
		const expandedTerms = Array.from(new Set(words.flatMap(expandTerms)));

		return categories
			.map((category) => {
				const categoryNameMatches = matchesSearch(category.name, expandedTerms);

				const matchedQuestions = allQuestions.filter(
					(q) =>
						q.category === category.id &&
						(matchesSearch(q.name ?? '', expandedTerms) ||
							matchesSearch(q.description ?? '', expandedTerms))
				);

				return { category, matchedQuestions, categoryNameMatches };
			})
			.filter((r) => r.categoryNameMatches || r.matchedQuestions.length > 0);
	}, [searchTerm, categories, allQuestions, isSearchActive]);

	useEffect(() => {
		let t: number | undefined;
		if (isSearchActive) {
			setSearchDelayActive(true);
			t = window.setTimeout(() => setSearchDelayActive(false), 2000);
		} else {
			setSearchDelayActive(false);
		}

		return () => {
			if (t) clearTimeout(t);
		};
	}, [searchTerm, isSearchActive]);

	const matchingCategoryCount = searchResults.length;
	const totalQuestionCount = searchResults.reduce((sum, r) => sum + r.matchedQuestions.length, 0);

	return (
		<>
			{isOnHomePage && (
				<>
					<br />
					<br />
				</>
			)}

			<GridItem span={8}>
				<FormGroup>
					<Flex
						alignItems={{ default: 'alignItemsCenter' }}
						spaceItems={{ default: 'spaceItemsSm' }}
					>
						<FlexItem grow={{ default: 'grow' }}>
							<div style={{ position: 'relative' }}>
								{aiSearchEnabled && (
									<FontAwesomeIcon
										icon={faSparkles}
										style={{
											position: 'absolute',
											left: '36px',
											top: '20%',
											transform: 'translateY(-50%)',
											color: '#06c',
											zIndex: 99999,
											pointerEvents: 'none',
											fontSize: '22px',
										}}
									/>
								)}
								<SearchInput
									placeholder={
										aiSearchEnabled
											? 'Ask AI anything about your data...'
											: isSearchActive
											? 'Searching categories and questions...'
											: 'Type at least 3 characters to search...'
									}
									value={searchTerm}
									onChange={(_event, value: string) => setSearchTerm(value)}
									className={`${aiSearchEnabled ? 'hide-search-icon' : ''}`}
									onClear={() => setSearchTerm('')}
									onSearch={handleSearch}
									onKeyDown={handleKeyDown}
									aria-label="Discover Search"
									data-testid="discover-search"
									style={
										aiSearchEnabled
											? { paddingLeft: '30px', borderRadius: '5px' }
											: undefined
									}
								/>
								<div style={{ marginTop: '6px' }}>
									<Text
										component="small"
										style={{
											color: aiSearchEnabled ? '#06c' : '#666',
											fontWeight: aiSearchEnabled ? 600 : 400,
											whiteSpace: 'nowrap',
											transition: 'color 0.2s',
										}}
									>
										{aiSearchEnabled ? (
											<>
												<FontAwesomeIcon
													icon={faSparkles}
													style={{ marginRight: '4px', color: '#06c' }}
												/>
												AI Search Enabled
											</>
										) : (
											'AI Search Off'
										)}
									</Text>
									<br />
									<br />
								</div>
							</div>
						</FlexItem>
						<FlexItem>
							<Flex
								alignItems={{ default: 'alignItemsCenter' }}
								spaceItems={{ default: 'spaceItemsXs' }}
							>
								<FlexItem style={{ paddingBottom: '28px' }}>
									<Switch
										id="ai-search-toggle"
										aria-label="Toggle AI Search"
										isChecked={aiSearchEnabled}
										onChange={(checked) => setAiSearchEnabled(checked)}
									/>
								</FlexItem>
							</Flex>
						</FlexItem>
					</Flex>
				</FormGroup>
			</GridItem>

			{isSearchActive && !aiSearchEnabled && (
				<GridItem span={12}>
					<div className="discover-search-results">
						{searchDelayActive ? (
							<Loader />
						) : searchResults.length === 0 ? (
							<EmptyResults
								emptyStateBody={`No categories or questions matched "${searchTerm}"`}
								iconName={faInfoCircle}
								backComponent={<></>}
							/>
						) : (
							<>
								<Flex>
									<div className="filter-container">
										<div className="date-selector-container">
											<DatePeriodSelector applyDateRange={setSelectedDate} />
										</div>
									</div>
								</Flex>
								<br />
								<Text
									component="p"
									className="discover-search-summary"
								>
									<strong>{matchingCategoryCount}</strong>{' '}
									{matchingCategoryCount === 1 ? 'category' : 'categories'} found
									— <strong>{totalQuestionCount}</strong>{' '}
									{totalQuestionCount === 1 ? 'question' : 'questions'} matched
									across all categories
								</Text>
								<br />
								<div className="discover-search-categories">
									{searchResults.map((result) => {
										if (
											!result.matchedQuestions ||
											result.matchedQuestions.length === 0
										)
											return null;
										return (
											<div
												key={result.category.id}
												className="discover-search-category-block"
											>
												<Flex
													alignItems={{ default: 'alignItemsCenter' }}
													className="category-title"
												>
													<FlexItem>
														<FontAwesomeIcon
															icon={result.category.icon}
															style={{ width: 28, height: 28 }}
														/>
													</FlexItem>
													<FlexItem>
														<Title headingLevel="h3">
															{result.category.name}
														</Title>
													</FlexItem>
													<FlexItem>
														&mdash;{' '}
														<span className="discover-search-question-count">
															{result.matchedQuestions.length}{' '}
															{result.matchedQuestions.length === 1
																? 'question'
																: 'questions'}{' '}
															found
														</span>
													</FlexItem>
												</Flex>
												<div className="discover-search-category-questions">
													{result.matchedQuestions.map((q) => {
														const cq = {
															key: `Question_${q.name}`,
															question: q.name,
															description: q.description,
															priority: q.sequence,
															tags: q.tags
																? q.tags.map((t: TTag) =>
																		t.name ? t.name : t
																  )
																: [],
															hasChart: q.chart ? true : false,
															chartId: (q.chart ?? 0).toString(),
															dashboardId: (
																q.dashboard ?? 0
															).toString(),
														} as CategoryQuestion;
														return (
															<div
																className="categories"
																key={cq.key}
															>
																<Question
																	question={cq}
																	selectedDate={undefined}
																/>
															</div>
														);
													})}
												</div>
												<br />
											</div>
										);
									})}
								</div>
							</>
						)}
					</div>
				</GridItem>
			)}
		</>
	);
};

export default DiscoverSearch;
